import {makeAutoObservable, observable, runInAction} from "mobx";
import {injectable, inject} from "inversify";
import {ChallengeStatus} from "data/enums/challenge";
import {GameLevel} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IFriendsApiProvider} from "data/providers/api/friends.api.provider";
import {Empty} from "data/types/global";

export interface IFriend {
	friendId: number;
	displayName: string;
	isViewed: boolean;
	status: ChallengeStatus;
	level: GameLevel;
	isH2HChallenging: boolean;
}

export interface IFriendsStore {
	get friends(): IFriend[];

	get friendInvites(): IFriend[];

	fetch: () => Promise<IFriend[]>;
	fetchInvites: () => Promise<IFriend[]>;
	search: (code: string) => Promise<IFriend>;
	addFriend: (friend: IFriend) => Promise<void>;
	addFriendByCode: (friendCode: string, friend: IFriend) => Promise<void>;

	addFriendById: (friendId: number) => Promise<void>;

	requestPlay: (friendId: number) => Promise<void>;
	accept: (friend: IFriend) => Promise<void>;
	deleteFriend: (friendId: number) => Promise<void>;
	deleteFromFriendLists: (friendId: number) => void;

	getInviteByFriendId: (friendId: number) => Empty<IFriend>;
	moveToFriend: (friendId: number) => void;
}

@injectable()
export class FriendsStore implements IFriendsStore {
	@observable _list: IFriend[] = [];
	@observable _invitesList: IFriend[] = [];

	constructor(@inject(Bindings.FriendsApiProvider) private _apiProvider: IFriendsApiProvider) {
		makeAutoObservable(this);
	}

	get friends() {
		return this._list;
	}

	get friendInvites(): IFriend[] {
		return this._invitesList;
	}

	fetch = async () => {
		const result = await this._apiProvider.friends();
		runInAction(() => {
			this._list = result.data.success.friends;
		});

		return result.data.success.friends;
	};

	fetchInvites = async () => {
		const result = await this._apiProvider.friend_invites();
		runInAction(() => {
			this._invitesList = result.data.success.friends;
		});

		return result.data.success.friends;
	};

	async search(code: string) {
		const response = await this._apiProvider.search(code);
		return response.data.success.friend;
	}

	async addFriend(friend: IFriend) {
		await this._apiProvider.invite(friend.friendId);
		this._invitesList.push(friend);
	}

	async addFriendByCode(friendCode: string, friend: IFriend) {
		await this._apiProvider.invite_by_code(friendCode);
		this._list.push({
			...friend,
			status: ChallengeStatus.ACCEPTED,
		});
	}

	async addFriendById(friendId: number) {
		await this._apiProvider.invite(friendId);
	}

	requestPlay = async (_friendId: number) => {
		await Promise.resolve();
	};

	accept = async (friend: IFriend) => {
		await this._apiProvider.accept(friend.friendId);
		this.moveToFriend(friend.friendId);
	};

	deleteFriend = async (friendId: number) => {
		await this._apiProvider.delete(friendId);
		this.deleteFromFriendLists(friendId);
	};

	getInviteByFriendId = (friendId: number) => {
		const invite = this._invitesList.find((it) => it.friendId === friendId);
		const friend = this._list.find((it) => it.friendId === friendId);
		return friend || invite;
	};

	moveToFriend = (friendId: number) => {
		const friend = this.friendInvites.find((it) => it.friendId === friendId);
		if (friend) {
			this._list.push({
				...friend,
				status: ChallengeStatus.ACCEPTED,
			});
			this._invitesList = this._invitesList.filter((it) => it.friendId !== friend.friendId);
		}
	};

	deleteFromFriendLists = (friendId: number) => {
		this._invitesList = this._invitesList.filter((it) => it.friendId !== friendId);
		this._list = this._list.filter((it) => it.friendId !== friendId);
	};
}
